import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  Alert,
  Button,
  Card,
  DataTable,
  Icon,
  SectionCard,
  Tooltip,
  Typography,
  type ColumnDef,
} from "@/deps/ui-library";
import {
  chartTemplateQuery,
  useApplyChartTemplate,
  useChart,
  useChartTemplate,
  useChartTemplates,
} from "@/api/queries";
import type { AccountGroup, CoaTemplate, CoaTemplateAccount } from "@/api/types";
import {
  byClass,
  REQUIRED_TIP,
  statementLineName,
  SUGGESTED_LOCKED,
  templateOf,
} from "@/lib/chart";
import { ClassTables } from "../chart/ClassTables";
import { refusalMessage } from "../chart/refusal";
import { TemplatePicker, type ChosenSet } from "../chart/TemplatePicker";

/**
 * Onboarding's chart step (handoff §5): what the person has chosen, which nothing writes until the step is
 * saved. `undefined` is "untouched", which shows whatever the chart already holds and writes nothing.
 */
export function useChartStep() {
  const chart = useChart();
  const templates = useChartTemplates();
  const apply = useApplyChartTemplate();
  const queryClient = useQueryClient();
  const [choice, setChoice] = useState<ChosenSet | null | undefined>(undefined);

  // A template applied before (a step saved, then revisited) is read back from the accounts' source.
  const onChart = useMemo(() => {
    const accounts = chart.data?.accounts ?? [];
    const code = accounts.map(templateOf).find((c) => c !== null) ?? null;
    const template = templates.data?.find((t) => t.code === code) ?? null;
    return template
      ? {
          template,
          codes: new Set(accounts.filter((a) => templateOf(a) === code).map((a) => a.code)),
        }
      : null;
  }, [chart.data, templates.data]);
  const onChartDetail = useChartTemplate(
    onChart?.template.code ?? null,
    onChart?.template.version ?? null,
  );
  const saved: ChosenSet | null = useMemo(
    () =>
      onChart && onChartDetail.data
        ? {
            template: onChart.template,
            dropped: onChartDetail.data.accounts
              .map((a) => a.code)
              .filter((code) => !onChart.codes.has(code)),
          }
        : null,
    [onChart, onChartDetail.data],
  );

  const current = choice === undefined ? saved : choice;
  const ledgerHasPostings = chart.data?.ledgerHasPostings ?? false;

  /** Writes the chosen set, when there is one to write. Called by the wizard's Save and continue. */
  const commit = async () => {
    if (choice === undefined || ledgerHasPostings) return;
    if (choice === null) {
      if (!saved) return;
      // Remove all after a set was applied: apply it again with every one of its accounts left out.
      const detail = await queryClient.fetchQuery(
        chartTemplateQuery(saved.template.code, saved.template.version),
      );
      await apply.mutateAsync({
        code: saved.template.code,
        version: saved.template.version,
        excludeCodes: detail.accounts.map((a) => a.code),
      });
      return;
    }
    const unchanged =
      saved?.template.code === choice.template.code &&
      saved.dropped.length === choice.dropped.length &&
      saved.dropped.every((code) => choice.dropped.includes(code));
    if (unchanged) return;
    await apply.mutateAsync({
      code: choice.template.code,
      version: choice.template.version,
      excludeCodes: choice.dropped,
    });
  };

  return {
    chart,
    current,
    ledgerHasPostings,
    choose: (template: CoaTemplate) => setChoice({ template, dropped: [] }),
    drop: (code: string) =>
      current && setChoice({ ...current, dropped: [...current.dropped, code] }),
    restore: () => current && setChoice({ ...current, dropped: [] }),
    removeAll: () => setChoice(null),
    commit,
    saving: apply.isPending,
    error: refusalMessage(apply.error),
    /** What the chart holds now, for the Done screen (handoff §5.7). */
    summary: onChart
      ? `${onChart.template.name} chart of accounts (${onChart.codes.size} accounts)`
      : `required accounts only (${(chart.data?.accounts ?? []).filter((a) => a.isRequired).length})`,
  };
}

export type ChartStepState = ReturnType<typeof useChartStep>;

type RequiredRow =
  | { kind: "bank"; code: string; group: AccountGroup; name: string; line: string }
  | {
      kind: "account";
      code: string;
      group: AccountGroup;
      name: string;
      line: string;
      reason: string;
    };

const REQUIRED_COLUMNS: Array<ColumnDef<RequiredRow>> = [
  { id: "code", header: "Code", size: 96, cell: ({ row }) => row.original.code },
  {
    id: "name",
    header: "Account",
    cell: ({ row }) =>
      row.original.kind === "bank" ? (
        <span className="text-muted-foreground">{row.original.name}</span>
      ) : (
        row.original.name
      ),
  },
  {
    id: "line",
    header: "Statement line",
    size: 220,
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.line}</span>,
  },
  {
    id: "lock",
    header: "",
    size: 48,
    cell: ({ row: { original } }) =>
      original.kind === "account" ? (
        <span className="flex justify-end pr-1.5">
          <Tooltip content={`${REQUIRED_TIP} ${original.reason}`}>
            <span
              role="img"
              aria-label={`${original.code} is a required account`}
              className="text-muted-foreground inline-flex"
            >
              <Icon name="Lock" size="sm" />
            </span>
          </Tooltip>
        </span>
      ) : null,
  },
];

function suggestedColumns(onDrop: (code: string) => void): Array<ColumnDef<CoaTemplateAccount>> {
  return [
    { id: "code", header: "Code", size: 96, cell: ({ row }) => row.original.code },
    { id: "name", header: "Account", cell: ({ row }) => row.original.name },
    {
      id: "line",
      header: "Statement line",
      size: 220,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {statementLineName(row.original.statementLine)}
        </span>
      ),
    },
    {
      id: "drop",
      header: "",
      size: 48,
      cell: ({ row: { original } }) => (
        <span className="flex justify-end">
          <Tooltip content="Leave out">
            <Button.IconButton
              icon="X"
              iconVariant="cell"
              variant="secondary"
              size="xs"
              aria-label={`Leave out ${original.code} ${original.name}`}
              onClick={() => onDrop(original.code)}
            />
          </Tooltip>
        </span>
      ),
    },
  ];
}

/** The step's body: the required accounts, locked, then the suggested set on top of them. */
export function ChartStep({ state }: { state: ChartStepState }) {
  const [picking, setPicking] = useState(false);
  const { current } = state;
  const detail = useChartTemplate(
    current?.template.code ?? null,
    current?.template.version ?? null,
  );
  const accounts = state.chart.data?.accounts;

  const required = useMemo<RequiredRow[]>(
    () => [
      {
        kind: "bank",
        code: "1000–1099",
        group: "Assets",
        name: "Bank and cash accounts · Added in the Bank and cash step",
        line: statementLineName("cash_and_cash_equivalents"),
      },
      ...(accounts ?? [])
        .filter((a) => a.isRequired)
        .map((a): RequiredRow => ({
          kind: "account",
          code: a.code,
          group: a.group,
          name: a.name,
          line: statementLineName(a.statementLine),
          reason: a.requiredReason ?? "",
        })),
    ],
    [accounts],
  );
  const requiredCount = required.length - 1;

  const kept = useMemo(
    () =>
      current && detail.data
        ? detail.data.accounts.filter((a) => !current.dropped.includes(a.code))
        : [],
    [current, detail.data],
  );
  const columns = useMemo(() => suggestedColumns(state.drop), [state.drop]);

  if (state.chart.isPending) {
    return <Typography.Text muted>Loading the chart</Typography.Text>;
  }
  if (state.chart.error) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        title="The chart of accounts couldn't be loaded"
        description={(state.chart.error as Error).message}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title={`Required accounts · ${requiredCount}`}>
        <div className="flex flex-col gap-3">
          <Typography.Text variant="small" muted className="m-0">
            Rigo posts to these automatically. They can't be edited, removed or made inactive.
          </Typography.Text>
          <ClassTables
            rows={required}
            columns={REQUIRED_COLUMNS}
            label={(group) => `Required ${group.toLowerCase()}`}
            rowId={(row) => row.code}
            count={(rows) => rows.filter((row) => row.kind === "account").length}
          />
        </div>
      </SectionCard>

      {current ? (
        <SectionCard
          title={`Suggested accounts · ${kept.length}`}
          actions={
            state.ledgerHasPostings ? undefined : (
              <span className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => setPicking(true)}>
                  Change template
                </Button>
                <Button variant="secondary" size="sm" onClick={state.removeAll}>
                  Remove all
                </Button>
              </span>
            )
          }
        >
          <div className="flex flex-col gap-3">
            <Typography.Text variant="small" muted className="m-0">
              {`From ${current.template.name}${current.template.basis ? ` · ${current.template.basis}` : ""}. Leave out anything you don't need; nothing is added until you save this step.`}
            </Typography.Text>
            {current.dropped.length > 0 ? (
              <span className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-foreground">
                  {`${current.dropped.length} removed from the suggested set.`}
                </span>
                <Button variant="ghost" colorScheme="primary" size="xs" onClick={state.restore}>
                  Restore
                </Button>
              </span>
            ) : null}
            {detail.data ? (
              <Accordion
                multiple
                className="max-w-none"
                defaultValue={[]}
                items={byClass(kept).map(({ group, accounts: rows }) => ({
                  value: group,
                  trigger: (
                    <span className="text-sm font-semibold">{`${group} · ${rows.length}`}</span>
                  ),
                  content: (
                    <DataTable
                      aria-label={`Suggested ${group.toLowerCase()}`}
                      size="sm"
                      enableSorting={false}
                      columns={columns}
                      data={rows}
                      getRowId={(row) => row.code}
                      pagination={{ mode: "manual", show: false }}
                    />
                  ),
                }))}
              />
            ) : (
              <Typography.Text variant="small" muted>
                Loading the accounts
              </Typography.Text>
            )}
          </div>
        </SectionCard>
      ) : (
        <Card className="border-border flex flex-col items-start gap-3 border-dashed shadow-none">
          <Typography.Text weight="semibold" className="m-0">
            Suggested accounts
          </Typography.Text>
          <Typography.Text variant="small" muted className="m-0">
            Add a ready-made set from NFRS for SMEs or IFRS for SMEs on top of the required
            accounts: revenue, expenses, fixed assets, payroll and the rest. They are ordinary
            accounts you can edit or leave out.
          </Typography.Text>
          {state.ledgerHasPostings ? (
            <Tooltip content={SUGGESTED_LOCKED} placement="bottom">
              <Button
                variant="secondary"
                size="sm"
                leftIcon="ListPlus"
                aria-disabled="true"
                onClick={(event) => event.preventDefault()}
                className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              >
                Add suggested accounts
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              leftIcon="ListPlus"
              onClick={() => setPicking(true)}
            >
              Add suggested accounts
            </Button>
          )}
          <Typography.Text variant="small" muted className="m-0">
            Building your own chart? Add at least one income and one expense account before the
            first invoice or bill, from Settings › Chart of accounts.
          </Typography.Text>
        </Card>
      )}

      {state.error ? (
        <Alert
          role="alert"
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={state.error}
        />
      ) : null}

      {picking ? (
        <TemplatePicker
          open
          mode="onboarding"
          current={current}
          onChoose={state.choose}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </div>
  );
}
